'use client'

/**
 * The app-wide skip link (#14982; docs/design-system/ADOPTION_DECISIONS.md
 * § 5, Option B). WCAG 2.4.1 Bypass Blocks, level A.
 *
 * The root layout renders this ONCE, first in `<body>`, so it is the first
 * Tab stop on every route — ahead of the feedback banner, the header's
 * primary navigation and the admin sidebar.
 *
 * ONE TARGET PER PAGE. The target is `main#main-content`. Pages own their
 * `<main>` — each renders its header beside it, so the root layout cannot
 * wrap them in one without nesting landmarks — and about 140 of them predate
 * the id. Rather than an id every future page must remember, the link
 * resolves its target when activated: the page's `#main-content` if it has
 * one (the admin layout's `<main>`, and the ~33 pages that already carry
 * it), otherwise the page's first `<main>` landmark. sai-ui's
 * `SkipToContent` handles the first case itself; `onClick` runs after it and
 * covers the second.
 */

import { SkipToContent } from '@sai/ui/components'
import type { MouseEvent } from 'react'
import { MAIN_CONTENT_ID } from './main-content-id'

/** The page's skip target: `#main-content`, else its first `<main>`. */
export function resolveSkipTarget(doc: Document): HTMLElement | null {
  return (
    doc.getElementById(MAIN_CONTENT_ID) ??
    doc.querySelector<HTMLElement>('main')
  )
}

function focusFallbackTarget(event: MouseEvent<HTMLAnchorElement>) {
  // sai-ui already focused `#main-content` when it exists.
  if (document.getElementById(MAIN_CONTENT_ID)) return
  event.preventDefault()
  const target = resolveSkipTarget(document)
  if (!target) return
  if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1')
  target.focus({ preventScroll: true })
  target.scrollIntoView({ block: 'start' })
}

export function SkipToMainContent() {
  return (
    <SkipToContent
      targetId={MAIN_CONTENT_ID}
      contentMessage="Skip to main content"
      data-testid="skip-to-main-content"
      onClick={focusFallbackTarget}
      // Above every layer, `z-toast` (100) included: a focused skip link must
      // never be obscured. Allow-listed in `layer-order-16990.test.ts`.
      className="focus:z-700"
    />
  )
}
